import { orderBy } from "lodash";
import {
  ConsultantOpportunityCardFragment,
  GetConsultantOpportunityBoardQuery
} from "@graphql";
import { BoardDescription, ColumnDescription } from "../kanban-board/kanban-types";
import { mapDataToBoard } from "../kanban-board/map-data-to-board";
import { moveCard } from "../kanban-board/move-card";
import { ConsultantOpportunityBoardFilter } from "./use-consultant-opportunity-board-context";
import { applyFilter } from "./consultant-opportunity-board-filters";
import { deleteCard } from "../kanban-board/delete-card";

type InitializeBoardAction = {
  type: "INITIALIZE_BOARD";
  payload: {
    response: GetConsultantOpportunityBoardQuery;
  };
};

type LoadCardsAction = {
  type: "LOAD_CARDS_ACTION";
  payload: {
    cards: ConsultantOpportunityCardFragment[];
  };
};

type RefetchBoardAction = {
  type: "REFETCH_BOARD";
  payload: {
    response: GetConsultantOpportunityBoardQuery;
  };
};

type MoveCardAction = {
  type: "MOVE_CARD";
  payload: {
    cardId: string;
    destColumnId: string;
    destPosition: number;
  };
};

type DeleteCardAction = {
  type: "DELETE_CARD";
  payload: {
    cardId: string;
  };
};

type FinalizePresentationAction = {
  type: "FINALIZE_PRESENTATION_ACTION";
  payload: {
    issueId: string;
    type: "won" | "lost";
  };
};

type EditFinalizedPresentationAction = {
  type: "EDIT_FINALIZED_PRESENTATION_ACTION";
  payload: {
    issueId: string;
  };
};

type CloseFinalizePresentationFormAction = {
  type: "CLOSE_FINALIZE_PRESENTATION_FORM";
};

type SucceedMoveCardToWinAction = {
  type: "SUCCEED_MOVE_CARD_TO_WIN";
};

type FilterChangeAction = {
  type: "FILTER_CHANGE";
  payload: {
    filter: ConsultantOpportunityBoardFilter;
  };
};

type ChangeBoardUserAction = {
  type: "CHANGE_BOARD_USER";
  payload: {
    userId: string;
    filter: ConsultantOpportunityBoardFilter;
  };
};

type OpenOpportunityFormAction = {
  type: "OPEN_UPDATE_OPPORTUNITY_FORM";
  payload: {
    issueId: string;
  };
};

type CloseUpdateOpportunityFormAction = {
  type: "CLOSE_UPDATE_OPPORTUNITY_FORM";
};

export type BoardAction =
  | InitializeBoardAction
  | LoadCardsAction
  | RefetchBoardAction
  | MoveCardAction
  | DeleteCardAction
  | FilterChangeAction
  | ChangeBoardUserAction
  | FinalizePresentationAction
  | EditFinalizedPresentationAction
  | CloseFinalizePresentationFormAction
  | SucceedMoveCardToWinAction
  | OpenOpportunityFormAction
  | CloseUpdateOpportunityFormAction;

export type BoardState = {
  board: BoardDescription<ConsultantOpportunityCardFragment>;
  initialBoard: BoardDescription<ConsultantOpportunityCardFragment>;
  cardIdColumnDataDictionary: Record<string, { id: number | string; title: string }>;
  lastActionType: string | null;
  finalizePresentationIssueId: string | null;
  finalizePresentationType: "won" | "lost" | null;
  updateOpportunityIssueId: string | null;
  filters: ConsultantOpportunityBoardFilter;
  boardUserId: string;
  untagConsultantId: string;
};

function createCardIdColumnDataDictionary(
  board: BoardDescription<ConsultantOpportunityCardFragment>
): Record<string, { id: number | string; title: string }> {
  const dictionary: Record<string, { id: number | string; title: string }> = {};

  board.columns.forEach((column) => {
    column.cards.forEach((card) => {
      dictionary[card.id] = { id: column.id, title: column.title };
    });
  });

  return dictionary;
}

export function consultantOpportunityBoardReducer(
  state: BoardState,
  action: BoardAction
) {
  if (action.type === "INITIALIZE_BOARD" || action.type === "REFETCH_BOARD") {
    if (!action.payload.response.kanbanBoard) {
      return {
        ...state,
        board: null,
        initialBoard: null,
        cardIdColumnDataDictionary: {}
      };
    }

    const initialState = mapDataToBoard(action.payload.response.kanbanBoard);
    const newBoardState = applyFilter(state.filters, initialState);

    return {
      ...state,
      board: newBoardState,
      initialBoard: newBoardState,
      cardIdColumnDataDictionary: {},
      lastActionType: action.type
    };
  } else if (action.type === "LOAD_CARDS_ACTION") {
    const board = applyCardsToBoard(action.payload.cards, state.board);
    return {
      ...state,
      board,
      cardIdColumnDataDictionary: createCardIdColumnDataDictionary(board)
    };
  } else if (action.type === "MOVE_CARD") {
    const newBoardState = moveCard(
      state.board,
      action.payload.cardId,
      action.payload.destColumnId,
      action.payload.destPosition
    );

    return {
      ...state,
      board: newBoardState,
      initialBoard: newBoardState,
      cardIdColumnDataDictionary: createCardIdColumnDataDictionary(newBoardState),
      lastActionType: action.type
    };
  } else if (action.type === "DELETE_CARD") {
    const newBoardState = deleteCard(state.board, action.payload.cardId);

    return {
      ...state,
      board: newBoardState,
      initialBoard: newBoardState,
      cardIdColumnDataDictionary: createCardIdColumnDataDictionary(newBoardState),
      lastActionType: action.type
    };
  } else if (action.type === "FINALIZE_PRESENTATION_ACTION") {
    return {
      ...state,
      finalizePresentationIssueId: action.payload.issueId,
      finalizePresentationType: action.payload.type
    };
  } else if (action.type === "EDIT_FINALIZED_PRESENTATION_ACTION") {
    return {
      ...state,
      finalizePresentationIssueId: action.payload.issueId
    };
  } else if (action.type === "SUCCEED_MOVE_CARD_TO_WIN") {
    return {
      ...state,
      contenderToWin: {
        issueId: null,
        boardBeforeMove: null
      }
    };
  } else if (action.type === "FILTER_CHANGE") {
    return {
      ...state,
      filters: action.payload.filter,
      board: applyFilter(action.payload.filter, state.initialBoard),
      lastActionType: action.type
    };
  } else if (action.type === "CLOSE_FINALIZE_PRESENTATION_FORM") {
    return {
      ...state,
      finalizePresentationIssueId: null,
      finalizePresentationType: null
    };
  } else if (action.type === "CHANGE_BOARD_USER") {
    return {
      ...state,
      boardUserId: action.payload.userId,
      filters: action.payload.filter
    };
  } else if (action.type === "OPEN_UPDATE_OPPORTUNITY_FORM") {
    return {
      ...state,
      updateOpportunityIssueId: action.payload.issueId
    };
  } else if (action.type === "CLOSE_UPDATE_OPPORTUNITY_FORM") {
    return {
      ...state,
      updateOpportunityIssueId: null
    };
  } else {
    return state;
  }
}

function applyCardsToBoard(
  cards: ConsultantOpportunityCardFragment[],
  state: BoardDescription<ConsultantOpportunityCardFragment>
) {
  const newState = { ...state };

  cards = orderBy(cards, [(x) => x.dueDate, (x) => x.title], ["desc", "asc"]);

  for (const column of newState.columns) {
    column.cards = [];
  }

  const columnsById = newState.columns.reduce((acc, column) => {
    acc[column.id] = column;
    return acc;
  }, {} as Record<string, ColumnDescription<ConsultantOpportunityCardFragment>>);

  for (const card of cards) {
    let column: ColumnDescription<ConsultantOpportunityCardFragment>;

    if (card.consultantOpportunity.weeksWon.length > 0) {
      column = columnsById["Won"];
    } else if (card.consultantOpportunity.weeksLost.length > 0) {
      column = columnsById["Lost"];
    } else {
      column = columnsById["Presented"];
    }

    if (column) {
      column.cards.push({
        id: card._id,
        title: card.title,
        issue: card
      });
    }
  }

  return newState;
}
